const escapeStringRegexp = require('escape-string-regexp')
const camelCase = require('camel-case')
const urlize = require('urlize')

const {
  chain,
  complement,
  defaultTo,
  is,
  isNil,
  path: ramdaPath,
  pickBy,
  reduce,
  toPairs,
  reduceIndexed,
  isEmpty,
  forEach,
  has,
  equals,
  replace,
  trim,
} = require('ramda')

const {
  I,
  compose,
  curry,
  difference,
  flip,
  propEq,
  eq,
} = require('yafu')

const mutatingUtils = require('./mutating-utils')

function endsWithY (string) {
  return string[string.length - 1] === 'y'
}

function replaceY (string) {
  return `${string.substring(0, string.length - 1)}ies`
}

const combiningDiacriticalMarksRegEx = /[\u0300-\u036f]/g

function stripDiacritics (str) {
  return str.normalize('NFD').replace(combiningDiacriticalMarksRegEx, '')
}

module.exports = function (R) {
  const endsWithId = R.test(/Id$/)

  function isIdKey (val, key) {
    return endsWithId(key) && val !== null
  }

  const u = {}

  u.LAUNCH_DATE = '2015-10-01'
  u.DATE_FORMAT = 'YYYY-MM-DD'
  u.TIMESTAMP_FORMAT = `${u.DATE_FORMAT}THH:mm:ssZ`
  u.TIMESTAMP_MS_FORMAT = `${u.DATE_FORMAT}THH:mm:ss:SSSZ`
  u.DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
  u.noop = function () { }
  u.hasId = propEq('_id')
  u.hasKey = propEq('key')
  u.hasName = propEq('name')

  u.pathEither = curry((p, a, b) => {
    const pa = R.path(p, a)
    const pb = R.path(p, b)
    return defaultTo(pb, pa)
  })

  u.appendTo = flip(R.append)

  u.pluralize = function (string) {
    if (endsWithY(string)) {
      return replaceY(string)
    }
    return `${string}s`
  }

  u.findWithId = R.useWith(R.find, propEq('_id'))
  u.findWithKey = R.useWith(R.find, propEq('key'))

  u.generateId = function () {
    return Math.random().toString().substring(2)
  }

  u.addGeneratedId = (obj) => R.assoc('_id', u.generateId(), obj)

  u.containsCaseInsensitive = R.curry((part, full) =>
    (R.isNil(full) ? false : R.indexOf(R.toLower(part), R.toLower(full)) !== -1))

  u.mapIndexed = R.addIndex(R.map)
  u.forEachIndexed = R.addIndex(R.forEach)

  u.sortByName = R.sortBy(R.compose(R.toLower, R.prop('name')))

  u.nameContains = R.curry((matchString, entity) => {
    const inputString = R.defaultTo('', matchString)
    const regexp = new RegExp(escapeStringRegexp(inputString), 'i')
    return R.test(regexp, entity.name)
  })

  u.nameMatches = R.curry((input, entity) => {
    const testString = stripDiacritics(R.toLower(R.trim(R.defaultTo('', input))))
    const entityName = stripDiacritics(R.toLower(R.trim(entity.name)))
    return R.identical(entityName, testString)
  })

  u.arrayToSet = (array) => {
    const set = new Set()
    array.forEach(set.add.bind(set))
    return set
  }

  u.filterByKey = R.curry((key, valueList, list) => {
    const tmpSet = u.arrayToSet(valueList)
    return R.filter((item) => tmpSet.has(item[key]), list)
  })

  u.rejectByProps = R.curry((prop, valueList, list) => {
    const tmpSet = u.arrayToSet(valueList)
    return R.reject((item) => tmpSet.has(item[prop]), list)
  })

  u.replaceListValues = R.curry((list, newValue, valuesToReplace) => {
    const tmpSet = u.arrayToSet(valuesToReplace)
    return R.reduce((acc, item) => {
      if (tmpSet.has(item)) {
        acc.push(newValue)
      } else {
        acc.push(item)
      }
      return acc
    }, [], list)
  })

  u.anyHasName = R.useWith(R.any, u.hasName)

  const hasAnyFactory = curry((propName) => (list) => {
    if (R.isEmpty(list)) {
      return R.F
    }
    const set = u.arrayToSet(list)
    return (entity) => set.has(R.prop(propName, entity))
  })

  u.propEqAny = hasAnyFactory

  u.hasAnyName = hasAnyFactory('name')

  u.hasAnyId = hasAnyFactory('_id')

  u.sumProp = R.curry((prop, entities) => R.transduce(R.map(R.propOr(0, prop)), R.add, 0, entities))

  u.transduceList = R.curry((transduceFn, inputList) => {
    const appendToList = (list, item) => {
      list.push(item)
      return list
    }
    return R.transduce(transduceFn, appendToList, [], inputList)
  })

  u.getType = R.prop('type')

  u.changeKeyName = R.curry((from, to, obj) => {
    if (R.has(from, obj)) {
      const val = R.prop(from, obj)
      return R.assoc(to, val, R.dissoc(from, obj))
    }
    return obj
  })

  u.createKey = (name) => camelCase(urlize.urlize(name), null, true)

  u.updateAllByProp = R.curry((prop, list, newValueList) =>
    R.map((item) => {
      const updatedValue = R.find(propEq(prop, item[prop]), newValueList)
      return R.isNil(updatedValue)
        ? item
        : updatedValue
    }, list)
  )

  u.createPathLens = (path) => R.lens(
    R.path(path),
    R.assocPath(path)
  )

  u.pickDefined = pickBy(complement(isNil))

  u.lcPath = R.curry((path, obj) => {
    const lens = u.createPathLens(path)
    return lens.map(R.toLower, obj)
  })

  u.wrapAsArray = R.ifElse(Array.isArray, R.identity, R.of)

  u.omitKeysFromAll = (keysToOmit, dataList) =>
    (R.isEmpty(keysToOmit) ? dataList : R.map(R.omit(keysToOmit), dataList))

  const mergeWithAndClear = curry((mergeObj, target) => (
    u.pickDefined(R.merge(target, mergeObj))
  ))

  u.adjustById = R.curry((id, obj, list) => {
    const index = R.findIndex(u.hasId(id), list)
    return index === -1
      ? list
      : R.adjust(mergeWithAndClear(obj), index, list)
  })

  u.anyHasKey = R.useWith(R.any, R.compose(u.hasKey, u.createKey), R.identity)

  u.mergeWith = R.curry((fn, leftObj, rightObj) => {
    const result = {}

    R.forEach((key) => {
      result[key] = R.has(key, rightObj) ? fn(leftObj[key], rightObj[key]) : leftObj[key]
    }, R.keys(leftObj))

    R.forEach((key) => {
      if (!R.has(key, result)) {
        result[key] = rightObj[key]
      }
    }, R.keys(rightObj))

    return result
  })

  u.mergeAllWith = R.curry((fn, objList) => R.reduce(u.mergeWith(fn), {}, objList))

  u.hasSameKey = R.eqProps('key')
  u.hasSameId = R.eqProps('_id')

  u.moveListElement = (from, to, list) => {
    if (from === to) {
      return list
    }
    const element = list[from]
    const newList = R.remove(from, 1, list)
    newList.splice(to, 0, element)
    return newList
  }

  function minusOneToInfinity (n) {
    return n === -1 ? Infinity : n
  }

  u.createSupersetComparator = curry((getter, superset) => (a, b) => {
    const aIndex = R.findIndex(R.identical(getter(a)), superset)
    const bIndex = R.findIndex(R.identical(getter(b)), superset)
    return minusOneToInfinity(aIndex) - minusOneToInfinity(bIndex)
  })

  const sortBySuperset = curry(
    (getter, superset, subset) => R.sort(u.createSupersetComparator(getter, superset), subset)
  )

  u.sortBySuperset = sortBySuperset(I)
  u.sortPropBySuperset = compose(sortBySuperset, R.prop)
  u.createSupersetPropComparator = compose(u.createSupersetComparator, R.prop)

  u.differenceByProp = R.curry(
    (prop, valueList, objList) => difference(valueList, R.pluck(prop, objList))
  )

  const isObject = R.both(
    R.is(Object),
    R.complement(Array.isArray)
  )

  function findStringsDeep (value) {
    if (isObject(value)) {
      return R.reduce((acc, val) => {
        const strings = findStringsDeep(val)
        mutatingUtils.pushAll(acc, strings)
        return acc
      }, [], R.values(value))
    }

    return u.wrapAsArray(value)
  }

  function findIdKeysDeep (obj) {
    const res = {}
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(obj)) {
      const value = obj[key]
      if (!isIdKey(value, key)) {
        if (isObject(value)) {
          const idKeys = findIdKeysDeep(value)
          mutatingUtils.mergeConcat(res, idKeys)
        }
        if (Array.isArray(value)) {
          const idKeys = u.findIdKeysFromList(value)
          mutatingUtils.mergeConcat(res, idKeys)
        }
      } else {
        const ids = findStringsDeep(value)
        if (isNil(res[key])) {
          res[key] = ids
        } else {
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < ids.length; i++) {
            res[key].push(ids[i])
          }
        }
      }
    }
    return res
  }

  u.findIdKeysFromList = (objList) => R.reduce((acc, item) => {
    const idKeyObj = findIdKeysDeep(item)
    R.forEach((pair) => {
      const [ idKey, value ] = pair
      const valueAsArray = u.wrapAsArray(value)
      const idsToAdd = difference(valueAsArray, R.defaultTo([], acc[idKey]))
      if (!R.isEmpty(idsToAdd)) {
        if (R.isNil(acc[idKey])) {
          acc[idKey] = idsToAdd
        } else {
          acc[idKey].push(...idsToAdd)
        }
      }
    }, R.toPairs(idKeyObj))
    return acc
  }, {}, objList)

  u.findIdKeys = (obj) => findIdKeysDeep(obj)

  u.isNaN = Number.isNaN

  u.mapPropToObj = curry((suffix, propName, list) => (
    R.reduce((acc, item) => {
      const key = `${R.prop(propName, item)}${suffix}`
      acc[key] = item
      return acc
    }, {}, list)
  ))

  u.mapIdToObj = u.mapPropToObj('', '_id')
  u.mapKeyToObj = u.mapPropToObj('', 'key')
  u.mapNameToObj = u.mapPropToObj('', 'name')
  u.mapValueToObj = u.mapPropToObj('', 'value')

  u.splitEvery = curry((sliceLength, list) => {
    const result = []
    for (let i = 0; i < list.length; i += sliceLength) {
      result.push(list.slice(i, (i + sliceLength)))
    }
    return result
  })

  u.flatten = chain(I)

  u.insertionSort = (compareFn, list) => {
    const from = 0
    const to = list.length
    const resultList = R.map(R.identity, list)
    for (let i = from + 1; i < to; i += 1) {
      const element = resultList[i]
      let j
      for (j = i - 1; j >= from; j -= 1) {
        const tmp = resultList[j]
        const order = compareFn(tmp, element)
        if (order > 0) {
          resultList[j + 1] = tmp
        } else {
          break
        }
      }
      resultList[j + 1] = element
    }
    return resultList
  }

  u.renameKeys = curry((keyNameMap, o) => (
    reduce((acc, currentKey) => {
      const newKey = keyNameMap[currentKey]
      const currentVal = o[currentKey]
      if (!isNil(newKey)) {
        acc[newKey] = currentVal
      } else if (isNil(acc[currentKey])) {
        acc[currentKey] = currentVal
      }
      return acc
    }, {}, Object.keys(o))
  ))

  u.numberOrZero = function (val) {
    return is(Number, val) ? val : 0
  }

  u.isRealNumber = function (n) {
    const num = parseInt(n, 10)
    return !Number.isNaN(num) && Number.isFinite(num)
  }

  u.splitAt = curry((index, arr) => {
    const left = arr.slice(0, index)
    const right = arr.slice(index)
    return [ left, right ]
  })

  u.leftPad = curry((char, length, val) => R.toString(val).padStart(length, char))

  u.pluckAllkeys = curry((m) => reduce((acc, item) => {
    const resolvedItem = m[item]
    if (isNil(resolvedItem)) return acc
    acc.push(resolvedItem)
    return acc
  }, []))

  u.pathOr = curry((d, p, l) => defaultTo(d, ramdaPath(p, l)))

  u.diffObjShallow = (a, b) => reduce((acc, [ key, aItem ]) => {
    const bItem = b[key]
    if (!bItem || aItem !== bItem) {
      acc[key] = aItem
    }
    return acc
  }, {}, toPairs(a))

  u.findAllIndexes = curry((pred, list) => reduceIndexed((acc, item, idx) => (
    pred(item)
      ? [ ...acc, idx ]
      : acc
  ), [], list))

  u.isEmptyProp = curry((key, obj) => isEmpty(obj[key]))

  u.pickFromMany = curry((props, objects) =>
    reduce((acc, object) => {
      forEach((prop) => {
        if (!has(prop, object)) return
        if (has(prop, acc)) {
          acc[prop].push(object[prop])
        } else {
          acc[prop] = [ object[prop] ]
        }
      }, props)
      return acc
    }, {}, objects))

  u.isEmptyObj = equals({})

  u.imply = (a, b) => !(a && !b)

  u.chainIndependent = ([ first, ...rest ]) =>
    reduce((acc, item) => (
      acc.chain(() => item)
    ), first, rest)

  u.reverseComparator = (originalComparator) => (a, b) => originalComparator(a, b) * -1

  u.removeZeroWidthSpace = replace(/\u200b/g, '')

  u.replaceNoBreakSpace = replace(/\u00a0/g, ' ')

  u.trimName = R.compose(
    trim,
    u.removeZeroWidthSpace,
    u.replaceNoBreakSpace
  )

  u.pathEq = curry((path, val, obj) => eq(val, ramdaPath(path, obj)))

  return u
}
